import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { playSound } from '../../utils/utils';
import './HealingFeedback.css';

const HealingFeedback = ({
  game,
  maxHp,
  reduceMotion,
  suspended = false,
  durationMs = 3000,
}) => {
  const previous = useRef({ hp: game.hp, maxHp, result: game.lastResult });
  const sequence = useRef(0);
  const [repair, setRepair] = useState(null);

  useEffect(() => {
    const before = previous.current;
    previous.current = { hp: game.hp, maxHp, result: game.lastResult };
    if (suspended || ['intro', 'opening', 'lost', 'won'].includes(game.phase)) {
      setRepair(null);
      return;
    }
    // Room damage can offset healing in the same update. Still announce the
    // actual repair, while showing the final HP after that room's damage.
    const roomHealing =
      game.lastResult !== before.result && !game.lastResult?.boss
        ? game.lastResult?.healed || 0
        : 0;
    const restored = Math.max(0, game.hp - before.hp, roomHealing);
    const expanded = Math.max(0, maxHp - before.maxHp);
    if (restored > 0 || expanded > 0) {
      if (expanded > 0)
        playSound('/sounds/rpg-bubble-power-up.mp3', {
          preventOverlap: true,
        });
      sequence.current += 1;
      setRepair({
        id: sequence.current,
        restored,
        expanded,
        hpBefore: before.hp,
        hpAfter: game.hp,
        maxHp,
        roomDamage: roomHealing > 0 && game.lastResult?.damage > 0,
      });
    } else if (game.hp < before.hp) {
      // Let the damage presentation take over when the Core retaliates.
      setRepair(null);
    }
  }, [game.hp, game.phase, game.lastResult, maxHp, suspended]);

  useEffect(() => {
    if (!repair) return undefined;
    const timer = setTimeout(() => setRepair(null), durationMs);
    return () => clearTimeout(timer);
  }, [repair, durationMs]);

  if (!repair || game.phase === 'won') return null;
  return createPortal(
    <motion.section
      key={repair.id}
      className={
        'coreboundHealingFeedback' + (reduceMotion ? ' is-reduced' : '')
      }
      role="status"
      aria-live="polite"
      initial={reduceMotion ? false : { opacity: 0, y: -18, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.25 }}
    >
      <div className="coreboundRepairEmblem" aria-hidden="true">
        <i />
        <i />
        <i />
        <svg viewBox="0 0 48 48">
          <path d="M24 39 8 24C-3 11 13 1 24 14 35 1 51 11 40 24Z" />
          <path d="M24 20V32M18 26H30" />
        </svg>
      </div>
      <div className="coreboundRepairReadout">
        <span>REPAIR PULSE RECEIVED</span>
        <strong>
          {repair.restored > 0
            ? '+' + repair.restored + ' HP'
            : '+' + repair.expanded + ' MAX HP'}
        </strong>
        {repair.restored > 0 && repair.expanded > 0 && (
          <b>+{repair.expanded} MAX HP</b>
        )}
      </div>
      <div className="coreboundRepairVitals">
        <span>
          {repair.roomDamage
            ? 'HP after room damage'
            : 'Your signal is stronger'}
        </span>
        <strong>
          {!repair.roomDamage && <>{repair.hpBefore} → </>}
          {repair.hpAfter} / {repair.maxHp} HP
        </strong>
      </div>
      <div className="coreboundRepairBar" aria-hidden="true">
        <motion.span
          initial={{
            width: (repair.hpBefore / repair.maxHp) * 100 + '%',
          }}
          animate={{ width: (repair.hpAfter / repair.maxHp) * 100 + '%' }}
          transition={{ duration: reduceMotion ? 0 : 0.7, delay: 0.15 }}
        />
      </div>
    </motion.section>,
    document.body
  );
};

export default HealingFeedback;
